import React, { useState } from 'react';

import { Stack } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import { useGetAccountChangeLog } from '~/hooks/apis/loyalty/useGetAccountChangeLog';

import type { AccountChange, AccountChangeAction } from '~/services/LuxLoyalty';
import { formatDateSlashesWithClock } from '~/services/TimeService';

import ErrorDisplay from '../Common/ErrorDisplay';

const DEFAULT_PAGE_SIZE = 25;

const ACTION_LABELS: Record<AccountChangeAction, string> = {
  ACCOUNT_CREATED: 'Account created',
  ACCOUNT_CLOSED: 'Account closed',
  ACCOUNT_REOPENED: 'Account re-opened',
  TIER_CHANGED: 'Tier changed',
  REGION_CHANGED: 'Region changed',
  AGENTHUB_LINKED: 'Agent hub linked',
  AGENTHUB_UNLINKED: 'Agent hub unlinked',
};

const columns: Array<GridColDef<AccountChange>> = [
  {
    field: 'createdAt',
    flex: 1,
    headerName: 'Date',
    sortable: false,
    valueFormatter: (value: string) => formatDateSlashesWithClock(value),
  },
  {
    field: 'action',
    flex: 1,
    headerName: 'Change',
    sortable: false,
    valueFormatter: (value: AccountChangeAction) => ACTION_LABELS[value] ?? value,
  },
  { field: 'previousValue', flex: 1, headerName: 'From', sortable: false },
  { field: 'newValue', flex: 1, headerName: 'To', sortable: false },
  { field: 'reason', flex: 2, headerName: 'Reason', sortable: false },
  {
    field: 'actorEmail',
    flex: 1.5,
    headerName: 'Changed by',
    sortable: false,
    valueGetter: (value, row) => value ?? row.actorId,
  },
];

interface Props {
  account: App.LuxLoyaltyAccount;
}

/** Lifecycle changes to the account (closures, re-opens, tier and region changes…), newest first. */
export default function LuxLoyaltyChangeLog({ account }: Props) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const { data, isFetching, error } = useGetAccountChangeLog(account.customerId, {
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
  });

  return (
    <Stack direction="column" gap={4} flex={1}>
      {error && <ErrorDisplay message={error.message} />}
      {!error && (
        <DataGrid
          density="standard"
          rows={data?.result.changes ?? []}
          columns={columns}
          loading={isFetching}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={data?.result.pageInfo.totalRecords ?? 0}
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
        />
      )}
    </Stack>
  );
}
